import React, { useEffect, useState } from 'react';
import { Link } from '@inertiajs/react';
import { LuX } from 'react-icons/lu';

import { ToastData, ToastVariant } from '@/types/toast';
import { TOAST_DURATION } from '@/enums/ToastVariant';

interface ToastProps extends Omit<ToastData, 'id'> {
  onDismiss: () => void;
  mobile?: boolean;
  style?: React.CSSProperties;
}

export default function Toast({
  message,
  action,
  variant,
  duration,
  onDismiss,
  mobile = false,
  style,
}: ToastProps) {
  const [progress, setProgress] = useState(100);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    const effectiveDuration = duration || TOAST_DURATION.DEFAULT;
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.max(0, 100 - (elapsed / effectiveDuration) * 100);
      setProgress(newProgress);
    }, 50);

    // Single timeout for the entire lifecycle
    const dismissTimeout = setTimeout(() => {
      clearInterval(interval);
      setProgress(0);

      // Wait 300ms with progress at 0, then hide
      setTimeout(() => {
        setIsVisible(false);
        setTimeout(onDismiss, 300); // Exit animation
      }, 300);
    }, effectiveDuration);

    return () => {
      clearInterval(interval);
      clearTimeout(dismissTimeout);
    };
  }, [duration, onDismiss]);

  const variantStyles: Record<ToastVariant, string> = {
    success: 'border-green-200 bg-green-50',
    info: 'border-blue-200 bg-blue-50',
    warning: 'border-yellow-200 bg-yellow-50',
    error: 'border-red-200 bg-red-50',
  };

  const progressColors: Record<ToastVariant, string> = {
    success: 'bg-green-600',
    info: 'bg-blue-600',
    warning: 'bg-yellow-600',
    error: 'bg-red-600',
  };

  return (
    <div
      className={`
        shadow-lg rounded-lg border transition-all duration-300 ease-in-out relative
        ${variantStyles[variant]}
        ${mobile ? 'w-full' : 'max-w-sm'}
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}
      `}
      style={style}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="p-4">
        <button
          onClick={onDismiss}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 p-1"
          aria-label="Dismiss notification"
        >
          <LuX className="w-4 h-4" />
        </button>
        <p className="text-sm text-gray-900 pr-8">{message}</p>
        {action && (
          <Link
            href={action.href}
            className="text-blue-600 hover:text-blue-500 text-sm block mt-1 underline"
            onClick={() => onDismiss()}
          >
            {action.text}
          </Link>
        )}
      </div>

      <div className="h-1 bg-gray-200">
        <div
          className={`h-full transition-all duration-100 ease-linear ${progressColors[variant]}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
